import { createBrowserRouter, Route, Routes, useRouteError } from "react-router-dom";
import { useEffect } from "react";

/* general */
import App from "../components/App";
import SignInPage from "../pages/SignIn";
import Error404Page from "../pages/Error404Page";
import Error400Page from "../pages/Error400Page";
import { RouteConstants } from "../constants/ApplicationConstants/RouteConstants";
import { LocalStorageConstants } from "../constants/ApplicationConstants/LocalStorageConstants";
import UserListPage from "../pages/UserManagement/UserListPage";
import MasterManagementPage from "../pages/MasterManagementPage";
import WebUserPage from "../pages/UserManagement/WebUserPage";
import Authentication from "../util/Authentication";
import DisplayQueueStatusPage from "../pages/DisplayQueueStatus";
import DisplayScheduleListPage from "../pages/DisplayScheduleListPage";
import ManualStorageRetrievalRequestPage from "../pages/ManualStorageRetrievalRequestPage";
import StoreOrRetrievalParking from "../components/ManualStorageRetrievalRequest/StoreOrRetrievalParking";
import EmergencyRetrivalRequestPage from "../pages/EmergencyRetrivalRequestPage";
import ParkingOccupancyMonitoringPage from "../pages/ParkingOccupancyMonitoringPage";
import DeadParkingUpdatePage from "../pages/DeadParkingUpdate";
import UserFeedbackPage from "../pages/UserFeedbackPage";
import ParkingHistoryListPage from "../pages/ParkingHistory";
import LocalStorageUtil from "../util/LocalStorageUtil";
import { Modules, Operations } from "../constants/DatabaseConstants/MasterConstant";
import SupportQueryPage from "../pages/SupportQueryPage";
import DashboardPage from "../pages/DashboardPage";
import NotificationPage from "../pages/NotificationPage";
import AddNotificationPage from "../pages/AddNotificationPage";
import ConcurrentRequestPage from "../pages/ConcurrentRequestPage";

import type { UserInterface } from "../shared/interface/auth/User.interface";
import ReportPage from "../pages/ReportPage/PeakPerHourReportPage";
import VisitorReportPage from "../pages/ReportPage/VisitorReportPage";
import AllUsersReportPage from "../pages/ReportPage/AllUsersReportPage";
import BookingRetrievalCountPage from "../pages/ReportPage/BookingRetrievalReport";
import AdvanceBookingRequest from "../components/AdvanceBookingRequest";
import AdvanceBookingRequestReport from "../components/Reports/AdvanceBookingRequestReport";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import { UserRoleDATA } from "../constants/DatabaseConstants/AuthConstants";
import IotHubLogsPage from "../pages/IotHubLogsPage";
import ClientParkingAvailabilityListPage from "../pages/ClientParkingAvailabilityListPage";
import ABRPenaltyCollectionReportPage from "../pages/ReportPage/ABRPenaltyCollectionReportPage";
import LocationStatusListPage from "../pages/LocationStatusList";

const getRoutesByAccessControl = (userData: UserInterface) => {
  const routes = [];

  /* dashboard */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.DASHBOARD, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.DEFAULT_HOME_PAGE.slice(1)}`,
      element: <DashboardPage />
    });

    routes.push({
      path: `${RouteConstants.CLIENT_PARKING_AVAILABILITY_LIST.slice(1)}`,
      element: <ClientParkingAvailabilityListPage />
    });
  }

  /* user-management */
  /* Define user management routes only if the user has any relevant access */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.USER_MANAGEMENT, Operations.READ)) {
    const userManagementRoutes = [];

    const hasReadAccess = Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.READ) || Authentication.hasModuleAndOperationAccess(userData, Modules.MOBILE_USER, Operations.READ);

    if (hasReadAccess) {
      userManagementRoutes.push({
        path: RouteConstants.USER_LIST_PAGE,
        element: <UserListPage />
      });
    }

    if (Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.CREATE)) {
      userManagementRoutes.push({
        path: "create-user-form/:add",
        element: <WebUserPage />
      });
    }

    if (Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.UPDATE)) {
      userManagementRoutes.push({
        path: "update-user-form/:update/:id",
        element: <WebUserPage />
      });
    }

    if (Authentication.hasModuleAndOperationAccess(userData, Modules.WEB_USER, Operations.READ)) {
      userManagementRoutes.push({
        path: "view-user-details/:view/:id",
        element: <WebUserPage />
      });
    }

    // Ensure the 404 route is added last
    userManagementRoutes.push({
      path: "*",
      element: <Error404Page />
    });

    // Add the routes to the main routing configuration
    routes.push({
      path: `${RouteConstants.USER_MANAGEMENT_PAGE.slice(1)}/*`,
      children: userManagementRoutes
    });
  }

  /* master-management */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MASTER_MANAGEMENT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.MASTER_MANAGEMENT_PAGE.slice(1)}/:master`,
      element: <MasterManagementPage />
    });
  }

  /* parking-management */

  /* display-queue-status */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ONGOING_LIST, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.DISPLAY_QUEUE_STATUS_PAGE.slice(1)}`,
      element: <DisplayQueueStatusPage />
    });
  }

  /* display-schedule-list */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.SCHEDULE_LIST, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.DISPLAY_SCHEDULE_LIST_PAGE.slice(1)}`,
      element: <DisplayScheduleListPage />
    });
  }

  /* manual-storage-retrieval-request */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MANUAL_BOOKING_RETRIEVAL, Operations.READ)) {
    routes.push(
      {
        path: `${RouteConstants.BOOKING_RETRIEVAL_REQUEST_PAGE.slice(1)}`,
        element: <ManualStorageRetrievalRequestPage />
      },
      {
        path: `${RouteConstants.BOOKING_RETRIEVAL_REQUEST_PAGE.slice(1)}/${RouteConstants.USER_FORM_ADD_PAGE}`,
        element: <StoreOrRetrievalParking />
      }
    );
  }

  /* emergency-retrieval-request */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.EMERGENCY_RETRIEVAL_REQUEST, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.EMERGENCY_RETRIEVAL_REQUEST_PAGE.slice(1)}`,
      element: <EmergencyRetrivalRequestPage />
    });
  }

  /* parking-occupancy-monitoring */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_OCCUPANCY_MONITORING, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.PARKING_OCCUPANCY_MONITORING_PAGE.slice(1)}`,
      element: <ParkingOccupancyMonitoringPage />
    });
  }

  /* dead-parking-update */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.DEAD_PARKING_UPDATE, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.DEAD_PARKING_UPDATE_PAGE.slice(1)}`,
      element: <DeadParkingUpdatePage />
    });
  }

  /* user-feedback */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.USER_FEEDBACK, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.USER_FEEDBACK.slice(1)}`,
      element: <UserFeedbackPage />
    });
  }
  /*Parking History */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_HISTORY, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.PARKING_HISTORY.slice(1)}`,
      element: <ParkingHistoryListPage />
    });
  }

  /* Maintenance Notification */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MESSAGING, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.MESSAGING.slice(1)}`,
      element: <NotificationPage />
    });
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.MESSAGING, Operations.CREATE)) {
    routes.push({
      path: `${RouteConstants.ADD_NOTIFICATION.slice(1)}`,
      element: <AddNotificationPage />
    });
  }

  /* support-query */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.SUPPORT_QUERY, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.SUPPORT_QUERY.slice(1)}`,
      element: <SupportQueryPage />
    });
  }

  /* report */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.REQUEST_PER_HOUR_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.REQUEST_PER_HOUR_REPORT.slice(1)}`,
      element: <ReportPage />
    });
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.VISITORS_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.VISITORS_REPORT.slice(1)}`,
      element: <VisitorReportPage />
    });
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ALL_USERS_PARKING_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.ALL_USERS_PARKING_REPORT.slice(1)}`,
      element: <AllUsersReportPage />
    });
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.PARKING_DATA_COUNTS_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.PARKING_DATA_COUNTS_REPORT.slice(1)}`,
      element: <BookingRetrievalCountPage />
    });
  }

  /* Advance Booking Request */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ADVANCE_BOOKING_REQUEST_LIST, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.ADVANCE_BOOKING_REQUEST.slice(1)}`,
      element: <AdvanceBookingRequest />
    });
  }
  /* Advance Booking Request Report  */
  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ADVANCE_BOOKING_REQUEST_LIST, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.ADVANCE_BOOKING_REQUEST_REPORT.slice(1)}`,
      element: <AdvanceBookingRequestReport />
    });
  }

  if (userData?.role === UserRoleDATA.PARI_ADMIN) {
    routes.push(
      {
        path: `${RouteConstants.CONCURRENT_REQUEST.slice(1)}`,
        element: <ConcurrentRequestPage />
      },
      {
        path: `${RouteConstants.IOTHUB_LOGS.slice(1)}`,
        element: <IotHubLogsPage />
      },
      {
        path: `${RouteConstants.LOCATION_STATUS_LIST.slice(1)}`,
        element: <LocationStatusListPage />
      }
    );
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ABR_PENALTY_COLLECTION_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.ABR_PENALTY_COLLECTION_REPORT.slice(1)}`,
      element: <ABRPenaltyCollectionReportPage />
    });
  }

  if (Authentication.hasModuleAndOperationAccess(userData, Modules.ABR_PENALTY_COLLECTION_REPORT, Operations.READ)) {
    routes.push({
      path: `${RouteConstants.ABR_PENALTY_COLLECTION_REPORT.slice(1)}`,
      element: <ABRPenaltyCollectionReportPage />
    });
  }

  return routes;
};

const AppRoutes = () => {
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;
  const routes = getRoutesByAccessControl(userData);

  return (
    <App>
      <Routes>
        {/* Dynamically generated routes */}
        {routes?.map((route, index) => (
          <Route key={index} path={route?.path} element={route?.element}>
            {route?.children && route?.children?.map((child, childIndex) => <Route key={childIndex} path={child?.path} element={child?.element} />)}
          </Route>
        ))}

        {/* Fallback route for 404 */}
        <Route path="*" element={<Error404Page />} />
      </Routes>
    </App>
  );
};

const ErrorPage = () => {
  let error = useRouteError();

  useEffect(() => {
    if (error instanceof Error) {
      localStorage.setItem(LocalStorageConstants.ERROR_MESSAGE, error.message);
    }
    window.location.href = RouteConstants.ERROR_PAGE;
  }, [error]);

  return <></>;
};

const routes = [
  {
    path: RouteConstants.ROOT_PAGE,
    element: <SignInPage />,
    errorElement: <ErrorPage />,
    index: true
  },
  {
    path: RouteConstants.ERROR_PAGE,
    element: <Error400Page />
  },
  {
    path: RouteConstants.PRIVACY_POLICY,
    element: <PrivacyPolicyPage />
  },
  {
    path: "/*",
    errorElement: <ErrorPage />,
    element: <AppRoutes />
  }
];

const AppRouter = createBrowserRouter(routes);

export default AppRouter;
